import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";

import MainContainer from "../../layout/MainContainer";

import {
  useAddDocumentMutation,
  useLazyDocumentDetailsQuery,
  useUpdateDocumentMutation,
} from "../../service/ducuments";
import { errorToast, successToast } from "../../helpers";
import { isString, isValidInput } from "../../utils/validations";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import Loader from "../../constants/Loader";
import { VISA_TYPES } from "../../utils/enums";

const AddDocuments = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [visaType, setVisaType] = useState("default");

  const handleVisaType = (event: SelectChangeEvent) => {
    setVisaType(event.target.value as string);
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [getDocumentDetails] = useLazyDocumentDetailsQuery();
  const [addDocument] = useAddDocumentMutation();
  const [updateDocument] = useUpdateDocumentMutation();

  const getDetails = async (id: number) => {
    setLoading(true);

    try {
      const response = await getDocumentDetails({
        id,
      }).unwrap();
      setLoading(false);
      if (response?.status === 200) {
        formik.setFieldValue("document_name", response?.data?.document_name);
        setVisaType(String(response?.data?.visa_type) || "default");
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      document_name: "",
    },

    validationSchema: Yup.object({
      document_name: Yup.string()
        .required("Document name is required")
        .min(2, "Minimum 2 characters are required"),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const body = {
        visa_type: Number(visaType),
        document_name: values.document_name,
      };

      try {
        setLoading(true);
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        let response;
        if (id) {
          response = await updateDocument({
            body: encryptedBody,
            id: Number(id),
          }).unwrap();
        } else {
          response = await addDocument(encryptedBody).unwrap();
        }

        if (response?.status === 200) {
          setLoading(false);
          if (id) {
            successToast("Document updated successfully");
          } else {
            successToast("Document added successfully");
          }
          navigate("/manage-documents");
        } else {
          setLoading(false);
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "");
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (id) {
      getDetails(Number(id));
    }
  }, [id]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? "Edit" : "Add"} Documents</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
            <span onClick={() => navigate("/manage-documents")}>Documents</span>{" "}
            - {id ? "Edit" : "Add"} Documents
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_blue" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Select Visa Type</label>
              <Select
                fullWidth
                labelId="visaType"
                id="visaType"
                value={visaType}
                onChange={handleVisaType}
                disabled={id ? true : false}
              >
                <MenuItem disabled value="default">
                  Select
                </MenuItem>

                <MenuItem value={VISA_TYPES.TOURIST_VISA}>Visit Visa</MenuItem>
                <MenuItem value={VISA_TYPES.STUDY_VISA}>Study Visa</MenuItem>
                <MenuItem value={VISA_TYPES.WORK_VISA}>Work Visa</MenuItem>
                <MenuItem value={VISA_TYPES.BUSINESS_VISA}>
                  Business Visa
                </MenuItem>
              </Select>
            </div>

            <div className="control_group w_50">
              <label>Document Name</label>
              <TextField
                variant="outlined"
                placeholder="Document Name"
                fullWidth
                hiddenLabel
                className="text_field"
                name="document_name"
                id="document_name"
                inputProps={{ maxLength: 40 }}
                value={formik.values.document_name}
                onChange={(val) => {
                  if (
                    isValidInput(val.target.value) &&
                    isString(val.target.value)
                  ) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.document_name && formik.errors.document_name
                }
              />
            </div>
          </div>

          <div className="form_end_btn">
            <Button className="btn btn_secondary" type="submit">
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddDocuments;
